import React, { useState, useEffect } from 'react';
import { ResizableBox } from 'react-resizable';
import { Upload, Save, AlertCircle, Image, CheckCircle, Clock, RotateCcw } from 'lucide-react';
import 'react-resizable/css/styles.css';
import AdminLayout from '../../components/admin/AdminLayout';
import { supabase } from '../../lib/supabaseClient';

interface LogoHistoryEntry {
  id: string;
  url: string;
  uploaded_at: string;
  file_name: string;
}

// ─── Logo uploader ────────────────────────────────────────────────────────────

function LogoSection() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 400, height: 80 });
  const [currentLogo, setCurrentLogo] = useState<string | null>(null);
  const [logoHistory, setLogoHistory] = useState<LogoHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    fetchCurrentLogo();
    fetchLogoHistory();
  }, []);

  const fetchCurrentLogo = async () => {
    const { data } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'logo_url')
      .maybeSingle();
    if (data?.value) {
      setCurrentLogo(data.value);
      setPreviewUrl(data.value);
    }
  };

  const fetchLogoHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await supabase.storage.from('media').list('', {
        limit: 50,
        sortBy: { column: 'created_at', order: 'desc' },
      });
      const logoFiles = (data ?? []).filter((f) => f.name.startsWith('logo-'));
      setLogoHistory(
        logoFiles.map((f) => ({
          id: f.id ?? f.name,
          file_name: f.name,
          url: supabase.storage.from('media').getPublicUrl(f.name).data.publicUrl,
          uploaded_at: f.created_at ?? '',
        }))
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/png') { setError('PNG files only.'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('Max file size is 2 MB.'); return; }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
  };

  const handleResize = (_e: React.SyntheticEvent, { size }: { size: { width: number; height: number } }) => {
    setDimensions({
      width: Math.max(200, Math.min(800, size.width)),
      height: Math.max(40, Math.min(160, size.height)),
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) { setError('Select a file first.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const fileName = `logo-${Date.now()}.png`;
      const { error: uploadErr } = await supabase.storage.from('media').upload(fileName, selectedFile);
      if (uploadErr) throw uploadErr;
      const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(fileName);
      const { error: settingsErr } = await supabase.from('settings').upsert({ key: 'logo_url', value: publicUrl, updated_at: new Date().toISOString() });
      if (settingsErr) throw settingsErr;
      await supabase.from('settings').upsert({ key: 'logo_dimensions', value: JSON.stringify(dimensions), updated_at: new Date().toISOString() });
      setCurrentLogo(publicUrl);
      setSuccess('Logo updated successfully.');
      setSelectedFile(null);
      fetchLogoHistory();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (url: string) => {
    await supabase.from('settings').upsert({ key: 'logo_url', value: url, updated_at: new Date().toISOString() });
    setCurrentLogo(url);
    setPreviewUrl(url);
    setSuccess('Logo restored.');
  };

  const fmt = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {(error || success) && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm ${error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
          {error ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
          <span>{error || success}</span>
          <button onClick={() => { setError(''); setSuccess(''); }} className="ml-auto text-current opacity-60 hover:opacity-100">&times;</button>
        </div>
      )}

      {/* Upload card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Platform Logo</h2>
          <p className="text-sm text-gray-500 mt-0.5">PNG only, max 2 MB. Drag the corner of the preview to resize.</p>
        </div>
        <div className="p-6 flex flex-col lg:flex-row gap-8">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-700 mb-3">Preview</p>
            <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center" style={{ minHeight: 200 }}>
              {previewUrl || currentLogo ? (
                <ResizableBox
                  width={dimensions.width}
                  height={dimensions.height}
                  onResize={handleResize}
                  minConstraints={[200, 40]}
                  maxConstraints={[700, 160]}
                >
                  <img src={previewUrl || currentLogo || ''} alt="Logo preview" className="w-full h-full object-contain" style={{ pointerEvents: 'none' }} />
                </ResizableBox>
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400 py-10">
                  <Image className="w-10 h-10" />
                  <span className="text-sm">No logo uploaded yet</span>
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
                <Upload className="w-4 h-4" />
                Select PNG File
                <input type="file" className="hidden" accept="image/png" onChange={handleFileSelect} />
              </label>
              {selectedFile && <span className="text-sm text-gray-500 truncate max-w-[180px]">{selectedFile.name}</span>}
              <button onClick={handleUpload} disabled={!selectedFile || loading} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-500 text-white text-sm font-medium hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <Save className="w-4 h-4" />
                {loading ? 'Uploading…' : 'Apply Logo'}
              </button>
            </div>
          </div>
          <div className="w-full lg:w-60 flex-shrink-0">
            <p className="text-sm font-medium text-gray-700 mb-3">Guidelines</p>
            <ul className="space-y-2 text-sm text-gray-600">
              {['PNG format for best quality', 'Keep file size under 2 MB', 'Use transparent background', 'Min width 200 px, max 800 px', 'Good contrast on light & dark backgrounds'].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-1.5 flex-shrink-0" />{t}
                </li>
              ))}
            </ul>
            {currentLogo && (
              <div className="mt-5 p-3 rounded-lg bg-teal-50 border border-teal-100">
                <p className="text-xs font-medium text-teal-700 mb-1">Active logo</p>
                <img src={currentLogo} alt="Active logo" className="max-h-10 object-contain" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Logo History</h2>
            <p className="text-sm text-gray-500 mt-0.5">Click Restore to reactivate a previous logo</p>
          </div>
          <button onClick={fetchLogoHistory} className="text-sm text-teal-600 hover:text-teal-700 font-medium">Refresh</button>
        </div>
        {historyLoading ? (
          <div className="px-6 py-10 flex items-center justify-center text-gray-400 gap-2 text-sm">
            <Clock className="w-4 h-4 animate-spin" />Loading…
          </div>
        ) : logoHistory.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-400">No logo uploads found</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {logoHistory.map((entry) => {
              const isActive = entry.url === currentLogo;
              return (
                <div key={entry.id} className={`flex items-center gap-4 px-6 py-4 transition-colors ${isActive ? 'bg-teal-50' : 'hover:bg-gray-50'}`}>
                  <div className="w-24 h-12 rounded-lg border border-gray-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img src={entry.url} alt={entry.file_name} className="max-w-full max-h-full object-contain p-1" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{entry.file_name}</p>
                    <p className="text-xs text-gray-500">{fmt(entry.uploaded_at)}</p>
                  </div>
                  {isActive ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-medium">
                      <CheckCircle className="w-3.5 h-3.5" />Active
                    </span>
                  ) : (
                    <button onClick={() => handleRestore(entry.url)} className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                      Restore
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Product image uploader ───────────────────────────────────────────────────

type ProductKey = 'reports' | 'perplexity180';

const PRODUCTS: { key: ProductKey; label: string; settingsKey: string; storagePrefix: string }[] = [
  { key: 'reports', label: 'Reports Detective', settingsKey: 'reports_detective_image', storagePrefix: 'reports' },
  { key: 'perplexity180', label: 'Perplexity 180', settingsKey: 'perplexity180_image', storagePrefix: 'perplexity180' },
];

function ProductImagesSection() {
  const [files, setFiles] = useState<Record<ProductKey, File | null>>({ reports: null, perplexity180: null });
  const [previews, setPreviews] = useState<Record<ProductKey, string | null>>({ reports: null, perplexity180: null });
  const [current, setCurrent] = useState<Record<ProductKey, string | null>>({ reports: null, perplexity180: null });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState<ProductKey | null>(null);

  useEffect(() => {
    fetchCurrentImages();
  }, []);

  const fetchCurrentImages = async () => {
    const [rd, p180] = await Promise.all(
      PRODUCTS.map((p) => supabase.from('settings').select('value').eq('key', p.settingsKey).maybeSingle())
    );
    setCurrent({ reports: rd.data?.value ?? null, perplexity180: p180.data?.value ?? null });
  };

  const handleFileSelect = (key: ProductKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('Max file size is 2 MB.'); return; }
    setFiles((f) => ({ ...f, [key]: file }));
    setPreviews((p) => ({ ...p, [key]: URL.createObjectURL(file) }));
    setError('');
  };

  const handleUpload = async (key: ProductKey) => {
    const file = files[key];
    if (!file) { setError('Select a file first.'); return; }
    const product = PRODUCTS.find((p) => p.key === key)!;
    setLoading(key); setError(''); setSuccess('');
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${product.storagePrefix}-${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('media').upload(fileName, file);
      if (uploadErr) throw uploadErr;
      const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(fileName);
      await supabase.from('settings').upsert({ key: product.settingsKey, value: publicUrl, updated_at: new Date().toISOString() });
      setCurrent((c) => ({ ...c, [key]: publicUrl }));
      setFiles((f) => ({ ...f, [key]: null }));
      setSuccess(`${product.label} image updated successfully.`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {(error || success) && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm ${error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
          {error ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
          <span>{error || success}</span>
          <button onClick={() => { setError(''); setSuccess(''); }} className="ml-auto opacity-60 hover:opacity-100">&times;</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {PRODUCTS.map((product) => {
          const preview = previews[product.key];
          const currentImg = current[product.key];
          const selectedFile = files[product.key];
          const isUploading = loading === product.key;

          return (
            <div key={product.key} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">{product.label}</h3>
                <p className="text-sm text-gray-500 mt-0.5">PNG, JPG, or WEBP — max 2 MB</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center" style={{ minHeight: 180 }}>
                  {preview || currentImg ? (
                    <img src={preview || currentImg || ''} alt={product.label} className="max-w-full max-h-48 object-contain rounded-lg p-2" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-400 py-8">
                      <Image className="w-8 h-8" />
                      <span className="text-sm">No image set</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
                    <Upload className="w-4 h-4" />
                    Select Image
                    <input type="file" className="hidden" accept="image/*" onChange={handleFileSelect(product.key)} />
                  </label>
                  {selectedFile && <span className="text-sm text-gray-500 truncate max-w-[150px]">{selectedFile.name}</span>}
                  <button
                    onClick={() => handleUpload(product.key)}
                    disabled={!selectedFile || isUploading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-500 text-white text-sm font-medium hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    {isUploading ? 'Uploading…' : 'Apply'}
                  </button>
                </div>
                <ul className="text-xs text-gray-500 space-y-1">
                  {['High-quality images recommended', 'Aspect ratio 16:9 preferred', 'Min 1280 × 720 px'].map((t) => (
                    <li key={t} className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-gray-400 mt-1.5 flex-shrink-0" />{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = 'logo' | 'product-images';

export default function LogoManagement() {
  const [tab, setTab] = useState<Tab>('logo');

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
            <Image className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Logo & Image Management</h1>
            <p className="text-sm text-gray-500">Manage your platform logo and product images</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {([
            { key: 'logo', label: 'Platform Logo' },
            { key: 'product-images', label: 'Product Images' },
          ] as { key: Tab; label: string }[]).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'logo' ? <LogoSection /> : <ProductImagesSection />}
      </div>
    </AdminLayout>
  );
}
